import { useState, useRef, useEffect } from 'react'

type OptionItem = string | { label: string; value: string }

interface SearchableSelectProps {
  id?: string
  value: string
  onChange: (value: string) => void
  options: OptionItem[]
  placeholder?: string
  style?: React.CSSProperties
  required?: boolean
}

function getLabel(o: OptionItem): string {
  return typeof o === 'string' ? o : o.label
}

function getValue(o: OptionItem): string {
  return typeof o === 'string' ? o : o.value
}

export default function SearchableSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Search...',
  style,
  required,
}: SearchableSelectProps) {
  // Resolve display text from the current value
  const displayText = (() => {
    const match = options.find((o) => getValue(o) === value)
    return match ? getLabel(match) : value
  })()

  const [query, setQuery] = useState(displayText)
  const [open, setOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const filtered = query
    ? options.filter((o) => getLabel(o).toLowerCase().includes(query.toLowerCase()))
    : options

  useEffect(() => {
    const match = options.find((o) => getValue(o) === value)
    setQuery(match ? getLabel(match) : value)
  }, [value, options])

  useEffect(() => {
    setHighlightIndex(0)
  }, [query])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        // Reset to display text if query doesn't match any option
        const match = options.find((o) => getLabel(o).toLowerCase() === query.toLowerCase())
        if (!match) {
          const current = options.find((o) => getValue(o) === value)
          setQuery(current ? getLabel(current) : value)
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [query, value, options])

  const select = (option: OptionItem) => {
    onChange(getValue(option))
    setQuery(getLabel(option))
    setOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true)
        e.preventDefault()
      }
      return
    }

    if (e.key === 'ArrowDown') {
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1))
      e.preventDefault()
    } else if (e.key === 'ArrowUp') {
      setHighlightIndex((i) => Math.max(i - 1, 0))
      e.preventDefault()
    } else if (e.key === 'Enter') {
      if (filtered[highlightIndex]) select(filtered[highlightIndex])
      e.preventDefault()
    } else if (e.key === 'Escape') {
      setOpen(false)
      const current = options.find((o) => getValue(o) === value)
      setQuery(current ? getLabel(current) : value)
    }
  }

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightIndex, open])

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          if (!e.target.value) onChange('')
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        style={style}
      />
      {open && filtered.length > 0 && (
        <ul
          ref={listRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            margin: 0,
            padding: 0,
            listStyle: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          {filtered.map((option, i) => (
            <li
              key={getValue(option)}
              onMouseDown={() => select(option)}
              onMouseEnter={() => setHighlightIndex(i)}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                backgroundColor: i === highlightIndex ? '#f0f0f0' : '#fff',
                fontSize: '0.9rem',
              }}
            >
              {getLabel(option)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
